import React from 'react';
import { FiCheck, FiAlertCircle, FiAlertTriangle, FiLoader, FiCircle, FiMinus } from 'react-icons/fi';

type Status = 'idle' | 'ready' | 'busy' | 'error' | 'success' | 'warning';

interface StatusIndicatorProps {
  status: Status;
  label?: string;
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
  pulse?: boolean;
  /** Show icon for double encoding (accessibility) */
  showIcon?: boolean;
}

/**
 * Status configuration using colorblind-safe palette from design tokens.
 * - Uses blue (profit) for positive states instead of green
 * - Uses orange (loss) for negative states instead of red
 * - Each status has an associated icon for double encoding (shape + color)
 */
const statusConfig: Record<Status, {
  color: string;
  bgColor: string;
  textColor: string;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
}> = {
  idle: {
    color: 'bg-status-idle',
    bgColor: 'bg-gray-500/20',
    textColor: 'text-gray-400',
    label: 'Idle',
    Icon: FiMinus,
  },
  ready: {
    // Using colorblind-safe blue instead of green
    color: 'bg-status-ready',
    bgColor: 'bg-profit/20',
    textColor: 'text-profit',
    label: 'Ready',
    Icon: FiCircle,
  },
  busy: {
    // Using orange (loss color) for busy state - visually distinct
    color: 'bg-status-busy',
    bgColor: 'bg-loss/20',
    textColor: 'text-loss',
    label: 'Busy',
    Icon: FiLoader,
  },
  error: {
    // Red for errors - using icon for double encoding
    color: 'bg-status-error',
    bgColor: 'bg-red-500/20',
    textColor: 'text-red-400',
    label: 'Error',
    Icon: FiAlertCircle,
  },
  success: {
    // Using colorblind-safe blue instead of green
    color: 'bg-status-success',
    bgColor: 'bg-profit/20',
    textColor: 'text-profit',
    label: 'Success',
    Icon: FiCheck,
  },
  warning: {
    // Yellow/amber for warnings - triangle icon for clarity
    color: 'bg-amber-500',
    bgColor: 'bg-amber-500/20',
    textColor: 'text-amber-400',
    label: 'Warning',
    Icon: FiAlertTriangle,
  }
};

const sizeConfig = {
  sm: {
    dot: 'w-1.5 h-1.5',
    icon: 'w-3 h-3',
    badge: 'px-1.5 py-0.5 text-xs',
    gap: 'gap-1'
  },
  md: {
    dot: 'w-2 h-2',
    icon: 'w-3.5 h-3.5',
    badge: 'px-2 py-0.5 text-xs',
    gap: 'gap-1.5'
  },
  lg: {
    dot: 'w-2.5 h-2.5',
    icon: 'w-4 h-4',
    badge: 'px-2.5 py-1 text-sm',
    gap: 'gap-2'
  }
};

export default function StatusIndicator({
  status,
  label,
  showLabel = false,
  size = 'md',
  pulse = false,
  showIcon = false
}: StatusIndicatorProps) {
  const config = statusConfig[status];
  const sizeStyles = sizeConfig[size];
  const displayLabel = label || config.label;
  const Icon = config.Icon;

  // Determine if we should auto-pulse based on status
  const shouldPulse = pulse || status === 'busy';

  // Render the visual indicator (dot or icon)
  const renderIndicator = () => {
    if (showIcon) {
      return (
        <span className={`relative flex items-center justify-center ${config.textColor}`}>
          <Icon className={`${sizeStyles.icon} ${status === 'busy' ? 'animate-spin' : ''}`} />
        </span>
      );
    }

    return (
      <span className="relative flex">
        <span className={`${sizeStyles.dot} rounded-full ${config.color}`} />
        {shouldPulse && (
          <span
            className={`absolute inset-0 ${sizeStyles.dot} rounded-full ${config.color} animate-ping opacity-75`}
          />
        )}
      </span>
    );
  };

  if (showLabel) {
    return (
      <span
        className={`inline-flex items-center ${sizeStyles.gap} ${sizeStyles.badge} ${config.bgColor} rounded-full font-medium`}
      >
        {renderIndicator()}
        <span className={config.textColor}>
          {displayLabel}
        </span>
      </span>
    );
  }

  return renderIndicator();
}

// Named export for tree-shaking
export { StatusIndicator };
